<template>
  <div class="pitch-freelancer-card">
    <WdsStack justify-space-between>
      <WdsAvatarLabel
        :path="getAvatarPath(avatarPath, globalId)"
        :initials="initials"
        :text="name"
        :sub-text="email"
        :text-link="profileUrl"
      >
        <span class="tw-inline-flex tw-items-center tw-gap-2">
          <WdsButtonGhost v-if="profileUrl" secondary :href="profileUrl">{{ name }}</WdsButtonGhost>
          <template v-else>{{ name }}</template>
          <CandidateStageLabel :step="stage" size="sm" data-test="pitch-candidate-stage" />
        </span>
      </WdsAvatarLabel>
      <i class="close-icon zmdi zmdi-close" @click="removeFreelancer" />
    </WdsStack>
    <div v-if="mixedModeAllowed || showReferralFee" class="tw-flex tw-gap-x-4 tw-my-4">
      <div v-if="mixedModeAllowed" :class="showReferralFee ? 'tw-w-1/2' : 'tw-w-full'">
        <div class="f-500 tw-mb-2">{{ $t('Submission type') }} *</div>
        <WdsSelect
          :start-selected="candidate.workerType"
          :placeholder="$t('Select submission type')"
          :list="[
            { value: workerTypeConstants.MANAGED, label: $t('Managed') },
            { value: workerTypeConstants.REFERRED, label: $t('Referred') },
          ]"
          data-test="worker-type-select"
          @select-option="handleWorkerTypeSelected"
        />
        <div v-if="errors?.workerType?.at(0)" class="tw-text-red-500">{{ errors.workerType[0] }}</div>
      </div>
      <div v-if="showReferralFee" :class="mixedModeAllowed ? 'tw-w-1/2' : 'tw-w-full'">
        <div class="f-500 tw-mb-2">{{ $t('Referral fee') }} *</div>
        <InputCurrency
          v-model="referralFeeObj"
          :currency="currency"
          placeholder="00"
          data-test-prefix="referral-fee-"
          @update:model-value="updateReferralFee"
        />
        <div v-if="errors?.referralFee?.at(0)" class="tw-text-red-500">{{ errors.referralFee[0] }}</div>
      </div>
    </div>
    <div class="tw-flex tw-gap-x-4 tw-my-4">
      <div class="tw-w-1/2">
        <div class="f-500 tw-mb-2">{{ $t('Rate type') }} *</div>
        <WdsSelect
          :start-selected="candidate.rateType"
          :placeholder="$t('Select rate type')"
          :list="[
            { value: rateConstants.HOURLY, label: $t('Per hour') },
            { value: rateConstants.DAILY, label: $t('Per day') },
            { value: rateConstants.MONTHLY, label: $t('Per month') },
            { value: rateConstants.FIXED, label: $t('Fixed budget') },
          ]"
          @select-option="handleRateTypeSelected"
        />
        <div v-if="errors?.rateType?.at(0)" class="tw-text-red-500">{{ errors.rateType[0] }}</div>
      </div>
      <div class="tw-w-1/2">
        <div class="f-500 tw-mb-2">{{ $t('Worker rate') }} *</div>
        <InputCurrency
          v-model="rateObj"
          :currency="currency"
          placeholder="00"
          data-test-prefix="freelancer-rate-"
          @update:model-value="updateRate"
        />
        <div v-if="errors?.rate?.at(0)" class="tw-text-red-500">{{ errors.rate[0] }}</div>
      </div>
    </div>
    <div class="tw-bg-gray-50 tw-rounded tw-px-4 tw-py-3 tw-mb-4 tw-flex tw-items-center tw-gap-x-2">
      <WdsIcon name="dollar-sign" :size="16" />
      <span class="f-500">{{ $t('Total submission rate:') }}</span>
      <span class="f-500">{{ currency }} {{ totalSubmissionRate }} / {{ rateTypeLabel }}</span>
    </div>
    <div class="rate-title f-500">{{ $t('Availability') }}</div>
    <WdsRadioGroup v-model:checked="selectedAvailability" :name="`${globalId}-availability`">
      <WdsRadio :name="`${globalId}-availability`" value="immediately">
        {{ $t('Immediately') }}
      </WdsRadio>
      <WdsRadio :name="`${globalId}-availability`" value="starting_from">
        {{ $t('Fixed date') }}
      </WdsRadio>
    </WdsRadioGroup>
    <PickerDatetime
      v-if="selectedAvailability === 'starting_from'"
      v-model="availableFrom"
      as-input
      float-label
      format="YYYY-MM-DD"
      :label="$t('Starting from')"
      required
      class="tw-ml-6 tw-pl-0.5 tw-my-2"
      name="date_start"
    />
    <div class="f-500 tw-mb-2">{{ `${$t('CV')}${rateRequired ? '*' : ''}` }}</div>
    <div class="tw-ml-1.5 tw-mb-2">
      <div
        v-for="file in cvFiles"
        :key="file.globalId"
        class="tw-flex tw-items-center tw-gap-x-2 tw-mb-2"
      >
        <WdsIcon name="file" :size="16" />
        <a
          :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.localId })"
          class="tw-font-medium tw-grow"
          target="_blank"
        >
          {{ file.name }}
        </a>
        <WdsTag sm :label="$t('CV')" />
        <WdsIcon
          name="close"
          class="tw-cursor-pointer tw-justify-self-end tw-text-gray-600"
          @click="removeCvFile(file.globalId)"
        />
      </div>
    </div>
    <PickerFileFilebank
      v-slot="{ openFilePicker }"
      :exclude-ids="cvFiles.map(({ localId }) => localId)"
      @files-selected="uploadCvFiles"
    >
      <WdsButtonGhost primary class="tw-flex tw-items-center tw-gap-x-2" @click="openFilePicker">
        <WdsIcon name="upload" size="18" />
        {{ $t('Upload CV') }}
      </WdsButtonGhost>
    </PickerFileFilebank>
    <div v-if="errors?.cvFiles?.at(0)" class="tw-text-red-500">{{ errors.cvFiles[0] }}</div>
    <div class="f-500 tw-mb-2 tw-mt-4">{{ rateRequired ? $t('Additional attachments') : $t('Attachments') }}</div>
    <div class="tw-ml-1.5 tw-mb-2">
      <div
        v-for="file in files"
        :key="file.globalId"
        class="tw-flex tw-items-center tw-gap-x-2 tw-mb-2"
      >
        <WdsIcon name="file" :size="16" />
        <a
          :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.localId })"
          class="tw-font-medium tw-grow"
          target="_blank"
        >
          {{ file.name }}
        </a>
        <WdsIcon
          name="close"
          class="tw-cursor-pointer tw-justify-self-end tw-text-gray-600"
          @click="removeFile(file.globalId)"
        />
      </div>
    </div>
    <PickerFileFilebank
      v-slot="{ openFilePicker }"
      :exclude-ids="files.map(({ localId }) => localId)"
      @files-selected="uploadFiles"
    >
      <WdsButtonGhost primary class="tw-flex tw-items-center tw-gap-x-2" @click="openFilePicker">
        <WdsIcon name="upload" size="18" />
        {{ $t('Upload files') }}
      </WdsButtonGhost>
    </PickerFileFilebank>
    <div class="f-500 tw-mb-2 tw-mt-4">{{ $t('Links') }}</div>
    <div v-for="(link, index) in links" :key="link.key" class="tw-mb-2.5">
      <div class="tw-flex tw-items-center">
        <div class="tw-grow">
          <WdsInput v-model="links[index].url" prefix-icon="link" :name="link.key" />
        </div>
        <WdsIcon
          name="close"
          class="tw-cursor-pointer tw-text-gray-600 tw-ml-1"
          @click="removeLink(index)"
        />
      </div>
      <div v-if="errors?.[`links.${index}`]" class="tw-text-red-500">
        {{ errors[`links.${index}`][0] }}
      </div>
    </div>
    <div class="tw-mb-4">
      <WdsButtonGhost primary class="tw-flex tw-items-center tw-gap-x-2" @click="addLink('')">
        <WdsIcon name="plus" size="18" />
        {{ !links.length ? $t('Add link') : $t('Add another link') }}
      </WdsButtonGhost>
    </div>
    <div v-if="editingComment" class="comment-wrapper">
      <BaseTextareaAutoresize
        :id="`freelancer-comment-${globalId}`"
        ref="freelancerComment"
        v-model="comment"
        :name="`freelancer-comment-${globalId}`"
        additional-classes="freelancer-comment"
        :placeholder="`${$t('Add comment')}...`"
      />
      <WdsStack class="comment-actions" gap-sm>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsButton @click="cancelComment">{{ $t('Cancel') }}</WdsButton>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsButton primary size="sm" :disabled="comment === initialComment" @click="saveComment">
          {{ $t('Save') }}
        </WdsButton>
      </WdsStack>
    </div>
    <WdsStack
      v-else-if="comment"
      justify-space-between
      class="existing-comment"
      @click="openTextArea"
    >
      {{ comment }}
      <div v-click-outside="closeMenu" class="comment-menu" @click="openMenu">
        <i class="fa fa-ellipsis-v" />
        <div v-show="isMenuOpen" class="comment-menu-item">
          <WdsStack align-center gap-sm class="comment-menu-item-delete" @click="deleteComment">
            <i class="fa fa-trash-o" />
            <span>{{ $t('Delete comment') }}</span>
          </WdsStack>
        </div>
      </div>
    </WdsStack>
    <WdsStack v-else>
      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <WdsButton
        class="add-comment-button"
        data-test="add-comment"
        full-width
        @click="openTextArea"
      >
        {{ `${$t('Add comment')}...` }}
      </WdsButton>
    </WdsStack>
    <div v-if="errors?.comment?.at(0)" class="tw-text-red-500">{{ errors.comment[0] }}</div>
  </div>
</template>

<script>
import {
  WdsStack,
  WdsAvatarLabel,
  WdsRadio,
  WdsRadioGroup,
  WdsSelect,
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
  WdsTag,
} from '@worksome/wds-components'
import { nextTick } from 'vue'
import BaseTextareaAutoresize from '@/components/BaseTextareaAutoresize.vue'
import PickerDatetime from '@/components/PickerDatetime.vue'
import InputCurrency from '@/components/InputCurrency.vue'
import { getRateAsIntegerAndFraction } from '@/components/utilities/numberFormatting'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { deepClone } from '@/utils/tools'
import { getAvatarPath } from '@/utils/avatar'
import CandidateStageLabel from '@/components/Job/Candidates/CandidateStageLabel.vue'

export default {
  components: {
    PickerFileFilebank,
    PickerDatetime,
    WdsAvatarLabel,
    BaseTextareaAutoresize,
    InputCurrency,
    WdsStack,
    WdsRadio,
    WdsRadioGroup,
    WdsSelect,
    WdsButtonGhost,
    WdsIcon,
    WdsInput,
    WdsTag,
    CandidateStageLabel,
  },
  props: {
    globalId: {
      type: String,
      required: true,
    },
    avatarPath: {
      type: String,
      default: '',
    },
    initials: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      required: true,
    },
    initialComment: {
      type: String,
      default: '',
    },
    errors: {
      type: Object,
      default: () => {},
    },
    profileUrl: {
      type: String,
      default: '',
    },
    /** The candidate's hiring stage, as the GraphQL enum name. */
    stage: {
      type: String,
      default: '',
    },
    candidate: {
      type: Object,
      default: () => ({}),
    },
    rateRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the source company's organisation allows the supplier to mix
     * managed/referred workers. When false, the worker type select is hidden
     * and the value is forced to mirror `managesWorkers`.
     */
    mixedModeAllowed: {
      type: Boolean,
      default: false,
    },
    /**
     * Fallback flag used to determine the worker type when mixed mode is not
     * allowed: managed when the supplier manages workers, otherwise referred.
     */
    managesWorkers: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const rateObj = getRateAsIntegerAndFraction(this.candidate.rate)
    const referralFeeObj = getRateAsIntegerAndFraction(this.candidate.referralFee || 0)

    return {
      rateObj,
      referralFeeObj,
      namedRoutes,
      rateConstants: {
        HOURLY: 'HOURLY',
        DAILY: 'DAILY',
        MONTHLY: 'MONTHLY',
        FIXED: 'FIXED',
      },
      workerTypeConstants: {
        MANAGED: 'MANAGED',
        REFERRED: 'REFERRED',
      },
      comment: this.initialComment,
      editingComment: false,
      isMenuOpen: false,
      selectedAvailability: this.candidate.availableFrom ? 'starting_from' : 'immediately',
      links: !this.candidate.links?.length ? [] : deepClone([...this.candidate.links]),
      files: this.candidate.files ? [...this.candidate.files] : [],
      availableFrom: this.candidate.availableFrom,
      cvFiles: this.candidate.cvFiles ? [...this.candidate.cvFiles] : [],
    }
  },
  computed: {
    /**
     * Referral fee only applies to REFERRED submissions; for MANAGED workers
     * the staffing agency invoices direct so no per-submission referral
     * fee is captured.
     */
    showReferralFee() {
      return this.candidate.workerType === this.workerTypeConstants.REFERRED
    },
    totalSubmissionRate() {
      const rate = this.convertRateObjToDecimal(this.rateObj) || 0
      const fee = this.showReferralFee
        ? (this.convertRateObjToDecimal(this.referralFeeObj) || 0)
        : 0
      return (rate + fee).toFixed(2)
    },
    rateTypeLabel() {
      const labels = {
        HOURLY: this.$t('hour'),
        DAILY: this.$t('day'),
        MONTHLY: this.$t('month'),
        FIXED: this.$t('fixed'),
      }
      return labels[this.candidate.rateType] || this.$t('hour')
    },
  },
  watch: {
    links: {
      deep: true,
      handler(links) {
        this.$emit('update:candidate', { ...this.candidate, links })
      },
    },
    files: {
      deep: true,
      handler(files) {
        this.$emit('update:candidate', { ...this.candidate, files })
      },
    },
    cvFiles: {
      deep: true,
      handler(cvFiles) {
        this.$emit('update:candidate', { ...this.candidate, cvFiles })
      },
    },
    availableFrom(availableFrom) {
      this.$emit('update:candidate', { ...this.candidate, availableFrom })
    },
    selectedAvailability(value) {
      if (value === 'immediately') {
        this.$emit('update:candidate', { ...this.candidate, availableFrom: null })
      }
    },
  },
  created() {
    if (!this.mixedModeAllowed && !this.candidate.workerType) {
      const workerType = this.managesWorkers
        ? this.workerTypeConstants.MANAGED
        : this.workerTypeConstants.REFERRED
      this.$emit('update:candidate', { ...this.candidate, workerType })
    }
  },
  methods: {
    laravelNamedRoute,
    getAvatarPath,
    removeFreelancer() {
      this.$emit('removeFreelancer', this.globalId)
    },
    openTextArea() {
      this.editingComment = true
      nextTick(() => {
        this.$refs.freelancerComment.focus()
      })
    },
    saveComment() {
      this.editingComment = false
      this.$emit('update:candidate', { ...this.candidate, comment: this.comment || null })
    },
    cancelComment() {
      this.editingComment = false
      this.comment = this.initialComment
    },
    deleteComment(event) {
      // Stop propagation so that the text area does not open for editing
      event.stopPropagation()
      this.comment = null
      this.$emit('update:candidate', { ...this.candidate, comment: this.comment })
      this.isMenuOpen = false
    },
    openMenu(event) {
      // Stop propagation so that the text area does not open for editing
      event.stopPropagation()
      this.isMenuOpen = true
    },
    closeMenu() {
      this.isMenuOpen = false
    },
    addLink(url) {
      const hasEmptyRows = this.links.some((link) => link.url === '')

      if (!hasEmptyRows) {
        this.links = [...this.links, { key: this.links.length, url }]
      }
    },
    removeLink(linkIndex) {
      this.links.splice(linkIndex, 1)
    },
    handleWorkerTypeSelected({ value: workerType }) {
      const updated = { ...this.candidate, workerType }

      if (workerType === this.workerTypeConstants.MANAGED) {
        this.referralFeeObj = getRateAsIntegerAndFraction(0)
        updated.referralFee = null
      }

      this.$emit('update:candidate', updated)
    },
    updateReferralFee(newFee) {
      const feeAsDecimal = this.convertRateObjToDecimal(newFee) || 0
      this.$emit('update:candidate', { ...this.candidate, referralFee: feeAsDecimal })
    },
    handleRateTypeSelected({ value: rateType }) {
      if (!this.rateObj.fraction && !this.rateObj.integer) {
        if (rateType === this.rateConstants.HOURLY && this.candidate.hourRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.hourRate)
        } else if (rateType === this.rateConstants.DAILY && this.candidate.dailyRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.dailyRate)
        } else if (rateType === this.rateConstants.MONTHLY && this.candidate.monthlyRate) {
          this.rateObj = getRateAsIntegerAndFraction(this.candidate.monthlyRate)
        }
      }

      this.$emit('update:candidate', {
        ...this.candidate,
        rate: this.convertRateObjToDecimal(this.rateObj),
        rateType,
      })
    },
    updateRate(newRate) {
      const rateAsDecimal = this.convertRateObjToDecimal(newRate)
      this.$emit('update:candidate', { ...this.candidate, rate: rateAsDecimal })
    },
    uploadFiles(files) {
      this.files = [
        ...this.files,
        ...files.map((file) => ({
          name: file.name,
          localId: file.localId,
          globalId: file.globalId,
          storageName: file.storageName,
        })),
      ]
    },
    removeFile(fileGlobalId) {
      this.files = this.files.filter((file) => file.globalId !== fileGlobalId)
    },
    uploadCvFiles(files) {
      this.cvFiles = [
        ...this.cvFiles,
        ...files.map((file) => ({
          name: file.name,
          localId: file.localId,
          globalId: file.globalId,
          storageName: file.storageName,
        })),
      ]
    },
    removeCvFile(fileGlobalId) {
      this.cvFiles = this.cvFiles.filter((file) => file.globalId !== fileGlobalId)
    },
    convertRateObjToDecimal(rateObj) {
      return (
        (rateObj.integer || rateObj.fraction) &&
        parseFloat(`${rateObj.integer}.${rateObj.fraction}`)
      )
    },
  },
}
</script>

<style scoped lang="scss">
@import 'resources/sass/frontend/_color-variables';

.close-icon {
  flex-shrink: 0;
}

.pitch-freelancer-card {
  width: 48%;
  background-color: #ffffff;
  border-radius: 4px;
  padding: 24px;
  @apply tw-shadow-sm;
  margin-top: 28px;
}

.rate-title {
  margin-top: 16px;
  margin-bottom: 8px;
  text-transform: capitalize;
}

.freelancer-comment {
  min-height: 128px !important;
  padding-bottom: 55px !important;
}

.existing-comment {
  background-color: #f7f7f7;
  padding: 8px 16px;
}

.comment-wrapper {
  position: relative;
  border-radius: 4px;

  .comment-actions {
    position: absolute;
    bottom: 10px;
    right: 10px;
  }
}

.rate-input-wrapper {
  width: 50%;
  margin-bottom: 16px;
}

.zmdi-close {
  background: $truegray-300;
  border-radius: 100%;
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: $truegray-400;
  }
}

.add-comment-button {
  justify-content: left !important;
}
.comment-menu {
  position: relative;
  cursor: pointer;
  // Increase clickable area of menu icon because it is a very small target otherwise
  &:after {
    content: '';
    position: absolute;
    left: -5px;
    right: -5px;
    top: -2px;
    bottom: 6px;
  }

  .comment-menu-item {
    position: absolute;
    background-color: white;
    border-radius: 4px;
    @apply tw-shadow-sm;
    right: 0px;
    padding: 8px;
    white-space: nowrap;

    .comment-menu-item-delete {
      background-color: white;
      border-radius: 4px;
      padding: 4px 8px;
      font-weight: 500;
      &:hover {
        background-color: $red-50;
        color: $red-500;
      }
    }
  }
}
</style>
